import { DAYS } from "../utils";

import type { Translate } from "@shared/i18n/translationKey";

const APPLICATION_JSON = "application/json";

const pad = (value: number) => String(value).padStart(2, "0");

// Fecha local → "yyyy-MM-ddTHH:mm:00" sin corrimiento de zona horaria.
/** Valores del formulario de postulacion. `vacancyId` llega como number en el test. */
export interface VacancyApplicationValues {
  firstName?: string;
  lastName?: string;
  email?: string;
  interviewDateTime?: Date;
  interviewDay?: string;
  interviewHour?: string;
  cover?: Blob;
}

/**
 * El error de postulacion llega en cuatro formas distintas segun de donde venga (validacion
 * del backend, mensaje plano, objeto de errores, o texto crudo). La funcion las navega a
 * mano, asi que el tipo se declara laxo a proposito: los cuatro casos estan cubiertos por
 * `vacancyApplication.test.ts`.
 */
export interface PostulationErrorPayload {
  message?: string;
  data?: Record<string, unknown> & { validationErrors?: Record<string, string[]> };
}

export interface PostulationError {
  /** Puede ser el objeto del backend o texto crudo. */
  response?: { data?: PostulationErrorPayload | string };
}

export const toLocalISOString = (date: Date) => {
  const d = new Date(date);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
  );
};

// Fecha → "HH:mm".
export const toHourMinute = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

// Nombre del día (lunes-domingo) para una fecha; domingo (getDay 0) → índice 6.
export const dayNameFromDate = (date: Date) => {
  const day = date.getDay();
  // `getDay()` da 0-6 y el indice resultante tambien; DAYS trae los 7 dias.
  return DAYS[day === 0 ? 6 : day - 1]!;
};

// Arma el multipart (CV + JSON) para postular un candidato a una vacante.
export const buildVacancyApplicationFormData = (
  values: VacancyApplicationValues,
  vacancyId: string | number
) => {
  const application = {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    interviewDateTime: toLocalISOString(values.interviewDateTime as Date),
    interviewDay: values.interviewDay,
    interviewHour: values.interviewHour,
    vacancyIds: [vacancyId],
  };

  const formData = new FormData();
  formData.append("file", values.cover as Blob);
  formData.append(
    "vacancyApplication",
    new Blob([JSON.stringify(application)], { type: APPLICATION_JSON })
  );
  return formData;
};

// Extrae un mensaje legible del error de postulación, que llega en varias formas.
export const extractPostulationErrorMessage = (
  error: PostulationError,
  t: Translate
) => {
  const data = error.response?.data;
  // Alias estrechado para los accesos de objeto; `data` se sigue usando tal cual para el
  // caso de texto crudo, mas abajo. No cambia el orden de las comprobaciones.
  const payload: PostulationErrorPayload =
    typeof data === "object" && data !== null ? data : {};

  if (payload.data?.validationErrors) {
    return Object.entries(payload.data.validationErrors)
      .map(([field, errors]) => `${field}: ${errors.join(", ")}`)
      .join("\n");
  }
  if (!payload.data && !payload.message) return t("errorPostulating");
  if (payload.data && typeof payload.data === "object" && !payload.message) {
    return Object.values(payload.data).join("\n");
  }
  if (payload.message) return payload.message;
  if (typeof data === "string") return data;
  return t("errorPostulating");
};
